import TranslationsList from '~/components/translations-list/translations-list'
import CanvasView from '~/components/canvas-view/canvas-view'
import styles from '~/components/translation-view/translation-view.module.scss'
import { Component, Show, createSignal, onMount } from 'solid-js'
import { TranslationViewProps } from '~/components/translation-view/translation-view.interfaces'
import SidebarHeader from '~/components/sidebar-header/sidebar-header'
import { loadTranslations } from '~/services/current-document/loading/translations'
import { DocumentProps } from '~/editor/document/document.interfaces'
import { chapter, getChapterData, setChapter } from '~/services/current-document/chapter'
import Document from '~/editor/document/document'
import { getTranslations } from '~/services/database/translations'
import { getComments } from '~/services/database/comments'
import { transcribe } from '~/services/current-document/process/transcribe'
import ValidateChapterStep from '~/components/validate-chapter-step/validate-chapter-step'
import SmallButton from '~/components/small-button/small-button'
import ExportIcon from '~/assets/icons/export.svg'
import { setLoadingState } from '~/services/loading/loading'
import { translations } from '~/translations'
import ExportTranslationsModal from '~/components/export-translations-modal/export-translations-modal'
import ConfirmationModal from '~/components/confirmation-modal/confirmation-modal'
import Translation from '~/editor/translation/translation'
import { deleteTranslation } from '~/services/current-document/translations'
import { registerTranslationListRef } from '~/services/current-document/translations-list'

export const [translationsMarkedForDeletion, setTranslationsMarkedForDeletion] = createSignal<Translation[]>([])

const TranslationView: Component<TranslationViewProps> = ({ chapterId }) => {
  const [showExportTranslationsModal, setExportTranslationsModal] = createSignal(false)
  const [document, setDocument] = createSignal<Document | null>(null)

  onMount(() => {
    setLoadingState({
      loading: true
    })
  })

  const setIsLoading = (value: boolean) => {
    setLoadingState({
      loading: value
    })
  }

  const onDocumentReady = async (props: DocumentProps) => {
    const currentDocument = new Document(props)
    const chapterData = await getChapterData(chapterId)
    setChapter(chapterData)
    let translationsData = await getTranslations(chapterId)
    if (translationsData.length === 0){
      setLoadingState({
        loading: true,
        message: translations().loading.transcription
      })
      const result = await transcribe({
        id: chapterId
      })
      if (result?.success) {
        translationsData = await getTranslations(chapterId)
      }
    }   
    const commentsData = await getComments(chapterId)
    loadTranslations({
      currentDocument,
      chapterData,
      translationsData,
      commentsData,
      setIsLoading
    })

    setDocument(currentDocument)

    return currentDocument
  }

  const onExportTranslations = async () => {
    setExportTranslationsModal(true)
  }

  // Delete translations confirmation modal
  const onConfirmDeleteTranslation = async () => {
    const translations = translationsMarkedForDeletion()
    if (translations && translations.length > 0) {
      translations.forEach(async (translation) => {
        await deleteTranslation(translation, {
          database: true,
          view: true,
          canvas: true,
          history: translation.createHistoryEntryOnDelete
        })
      })
      setTranslationsMarkedForDeletion([])
      const doc = document()
      if (doc){
        doc.hasFocus = true
      }
    }
  }

  const onSetOpen = () => {
    setTranslationsMarkedForDeletion([])
    return false
  }
  
  return (
    <div class={styles.translationView}>
      <ConfirmationModal 
        title={translations().editor.translation.entries.deleteConfirmation.title}
        onConfirm={onConfirmDeleteTranslation}
        open={translationsMarkedForDeletion().length > 0}
        setOpen={onSetOpen}
      />

      <ExportTranslationsModal
        open={showExportTranslationsModal()}
        setOpen={setExportTranslationsModal}
        chapterId={chapterId}
      />

      <CanvasView
        settings={{
          mode: 'translation'
        }}
        onReady={onDocumentReady}
      />
      <div class={styles.sideView} ref={(e) => registerTranslationListRef(e)}>
        <Show when={chapter()}>
          {chapter => (
            <SidebarHeader
              chapter={chapter()}
              mode='translation'
            />
          )}
        </Show>

        <Show when={document()}>
          {document => <TranslationsList document={document()} />}
        </Show>

        <ValidateChapterStep
          mode='translation'
          aside={(
            <>
              <SmallButton
                icon={ExportIcon}
                onClick={onExportTranslations}
              >
                {translations().chapters.row.exportTranslation}
              </SmallButton>
            </>
          )}
        />
      </div>
    </div>
  )
}

export default TranslationView